import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { NgxUiLoaderModule, NgxUiLoaderService } from 'ngx-ui-loader';

@Component({
  selector: 'app-category-management',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxUiLoaderModule],
  templateUrl: './category-management.component.html',
  styleUrls: ['./category-management.component.css']
})
export class CategoryManagementComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('tableContainer') tableContainer?: ElementRef<HTMLDivElement>;
  categories: any[] = [];
  filteredCategories: any[] = [];
  suppliers: any[] = [];
  filteredSuppliers: any[] = [];
  showSupplierDropdown = false;
  supplierSearchTerm = '';
  showAddForm = false;
  editingCategory: any = null;
  loading = false;
  formLoading = false;
  categoryForm = {
    name: '',
    description: '',
    suppliers: [] as string[]
  };
  formErrors = {
    name: '',
    description: '',
    image: ''
  };
  formSubmitted = false; // Track if form has been submitted to show errors
  successMessage = ''; // Success notification message
  errorMessage = ''; // Error notification message
  selectedImageFile: File | null = null;
  imagePreview: string = '';
  existingImageUrl: string = '';
  imageUploadError: string = ''; // Store upload error
  uploadedImageUrl: string = ''; // Store S3 URL after upload
  uploadedImageKey: string = ''; // Store S3 key after upload
  imageUploading: boolean = false; // Track upload status
  
  // Search and filters
  searchTerm = '';
  selectedStatus = '';
  showActionMenu: { [key: string]: boolean } = {};
  showDeleteModal = false; // Track delete confirmation modal
  categoryToDelete: string | null = null; // Store category ID to delete
  showStatusModal = false; // Track status change success modal
  statusMessage = ''; // Message to display in status modal
  showViewModal = false; // View-only category modal (Admin & Procurement Manager)
  viewingCategory: any = null; // Category data for view modal
  openedViewFromProductList = false; // When true, closing view modal redirects to product list
  
  // Pagination (Infinite Scroll)
  currentPage = 1;
  itemsPerPage = 5; // Show 5 items per load
  totalPages = 1;
  totalItems = 0;
  isLoadingMore = false; // Track if loading more data
  hasMoreData = true; // Track if there's more data to load

  constructor(
    private apiService: ApiService,
    private ngxLoader: NgxUiLoaderService,
    private cdr: ChangeDetectorRef,
    public authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  private scrollHandler: any = null;
  private documentClickHandler: any = null;

  ngOnInit() {
    this.loadCategories();
    this.loadSuppliers();

    // If navigated from product list with ?view=categoryId, open view modal for that category
    const viewId = this.route.snapshot.queryParamMap.get('view');
    if (viewId && this.canViewCategory()) {
      this.openedViewFromProductList = true;
      this.apiService.getCategory(viewId).subscribe({
        next: (response: any) => {
          // Interceptor wraps as { data }; backend returns { category, products }
          const data = (response && response.data !== undefined) ? response.data : response;
          const category = (data && data.category !== undefined) ? data.category : data;
          if (!category || !category._id) return;
          this.viewCategory({
            ...category,
            imageUrl: category.imageUrl || this.getImageUrl(category.image)
          });
          this.router.navigate([], { relativeTo: this.route, queryParams: {}, queryParamsHandling: '' });
          this.cdr.detectChanges();
        },
        error: () => {
          this.openedViewFromProductList = false;
          this.router.navigate([], { relativeTo: this.route, queryParams: {}, queryParamsHandling: '' });
        }
      });
    }

    // Add document click listener to close dropdown when clicking outside
    this.documentClickHandler = this.handleDocumentClick.bind(this);
    // Use capture phase to catch clicks before they propagate
    document.addEventListener('click', this.documentClickHandler, true);
  }

  loadSuppliers() {
    this.apiService.getSuppliers(1, 1000, '', 'Active').subscribe({
      next: (response) => {
        if (response.suppliers) {
          this.suppliers = response.suppliers.filter((sup: any) => sup.isActive);
        } else {
          this.suppliers = response.filter((sup: any) => sup.isActive);
        }
        // Ensure all suppliers have required fields for search
        this.suppliers = this.suppliers.map((sup: any) => ({
          ...sup,
          name: sup.name || '',
          email: sup.email || '',
          supplierId: sup.supplierId || '',
          companyName: sup.companyName || ''
        }));
        // Initialize filtered suppliers with all suppliers
        this.filteredSuppliers = [...this.suppliers];
        // Reset search term
        this.supplierSearchTerm = '';
        console.log('Suppliers loaded:', this.suppliers.length);
      },
      error: (err) => {
        console.error('Error loading suppliers:', err);
        this.suppliers = [];
        this.filteredSuppliers = [];
        this.supplierSearchTerm = '';
      }
    });
  }

  onSupplierSearchInput(event: any) {
    // Get value directly from input event
    const value = event.target.value || '';
    this.supplierSearchTerm = value;
    this.performSearch(value);
  }

  onSupplierSearch(searchTerm?: string) {
    // Use provided search term or current supplierSearchTerm
    const term = searchTerm !== undefined ? searchTerm : this.supplierSearchTerm;
    this.performSearch(term);
  }

  performSearch(searchTerm: string) {
    // Ensure suppliers array exists
    if (!this.suppliers || this.suppliers.length === 0) {
      this.filteredSuppliers = [];
      this.cdr.detectChanges();
      return;
    }

    // If search term is empty, show all suppliers
    if (!searchTerm || !searchTerm.trim()) {
      this.filteredSuppliers = [...this.suppliers];
      this.cdr.detectChanges();
      return;
    }
    
    const search = searchTerm.toLowerCase().trim();
    
    // Filter suppliers by name, email, supplierId, or companyName
    const filtered = this.suppliers.filter((supplier: any) => {
      // Get all searchable fields and convert to lowercase strings, handle null/undefined
      const name = (supplier?.name || '').toString().toLowerCase();
      const companyName = (supplier?.companyName || '').toString().toLowerCase();
      const email = (supplier?.email || '').toString().toLowerCase();
      const supplierId = (supplier?.supplierId || '').toString().toLowerCase();
      
      // Check if search term matches any field (partial match)
      return name.includes(search) || 
             companyName.includes(search) || 
             email.includes(search) || 
             supplierId.includes(search);
    });
    
    // Assign new array reference to trigger change detection
    this.filteredSuppliers = [...filtered];
    
    // Force change detection
    this.cdr.detectChanges();
    
    // Debug log
    console.log('Search term:', search, 'Filtered count:', this.filteredSuppliers.length, 'Total suppliers:', this.suppliers.length);
  }

  openSupplierDropdown() {
    if (!this.showSupplierDropdown) {
      // Use setTimeout to prevent immediate closing by document click handler
      setTimeout(() => {
        this.showSupplierDropdown = true;
        // Ensure filteredSuppliers is initialized with all suppliers
        if (this.suppliers && this.suppliers.length > 0) {
          this.filteredSuppliers = [...this.suppliers];
        } else {
          this.filteredSuppliers = [];
        }
      }, 0);
    }
  }

  toggleSupplierDropdown(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    // Use setTimeout to prevent immediate closing by document click handler
    setTimeout(() => {
      this.showSupplierDropdown = !this.showSupplierDropdown;
      
      if (this.showSupplierDropdown) {
        // Ensure filteredSuppliers is initialized with all suppliers
        if (this.suppliers && this.suppliers.length > 0) {
          this.filteredSuppliers = [...this.suppliers];
        } else {
          this.filteredSuppliers = [];
        }
      } else {
        // Reset filtered suppliers to show all when closed
        if (this.suppliers && this.suppliers.length > 0) {
          this.filteredSuppliers = [...this.suppliers];
        }
      }
    }, 0);
  }

  closeSupplierDropdown() {
    this.showSupplierDropdown = false;
    this.supplierSearchTerm = '';
  }

  onModalOverlayClick(event: Event) {
    // Only close modal if clicking directly on overlay, not on modal content
    const target = event.target as HTMLElement;
    const currentTarget = event.currentTarget as HTMLElement;
    
    // Find the modal-content element
    const modalContent = currentTarget.querySelector('.modal-content');
    
    // Check if the click is on the overlay itself (not on modal-content or its children)
    // If modal-content exists and contains the target, don't close
    if (modalContent && modalContent.contains(target)) {
      return;
    }
    
    // If click is on overlay itself, close the modal
    if (target === currentTarget || target.classList.contains('modal-overlay')) {
      // Close dropdown first
      this.closeSupplierDropdown();
      // Then close modal
      this.cancelEdit();
    }
  }

  getSelectedSuppliersText(): string {
    if (this.categoryForm.suppliers.length === 0) {
      return 'Search and Select supplier';
    }
    if (this.categoryForm.suppliers.length === 1) {
      return '1 Supplier is selected';
    }
    return `${this.categoryForm.suppliers.length} Suppliers are selected`;
  }

  getSupplierName(supplierId: string): string {
    const supplier = this.suppliers.find(s => s._id === supplierId);
    return supplier ? supplier.companyName : 'Unknown';
  }

  getSupplierId(supplierId: string): string {
    // Find supplier and return supplierId if available, otherwise return _id
    const supplier = this.suppliers.find(s => s._id === supplierId);
    if (supplier && supplier.supplierId) {
      return supplier.supplierId;
    }
    // Fallback to _id if supplierId doesn't exist
    return supplierId || '';
  }

  removeSupplier(supplierId: string) {
    const index = this.categoryForm.suppliers.indexOf(supplierId);
    if (index > -1) {
      this.categoryForm.suppliers.splice(index, 1);
    }
  }

  toggleSupplier(supplierId: string) {
    const index = this.categoryForm.suppliers.indexOf(supplierId);
    if (index > -1) {
      this.categoryForm.suppliers.splice(index, 1);
    } else {
      this.categoryForm.suppliers.push(supplierId);
    }
  }

  isSupplierSelected(supplierId: string): boolean {
    return this.categoryForm.suppliers.includes(supplierId);
  }

  ngAfterViewInit() {
    // Attach scroll listener after view is initialized
    this.attachScrollListener();
  }

  attachScrollListener() {
    if (this.tableContainer && this.tableContainer.nativeElement) {
      // Remove existing listener if any
      if (this.scrollHandler) {
        this.tableContainer.nativeElement.removeEventListener('scroll', this.scrollHandler);
      }
      
      // Add scroll event listener to table container with throttling
      this.scrollHandler = this.throttle(() => {
        this.onScroll();
      }, 200); // Throttle to 200ms to prevent too many calls
      
      this.tableContainer.nativeElement.addEventListener('scroll', this.scrollHandler, { passive: true });
      console.log('attachScrollListener: Scroll listener attached to table container');
    } else {
      console.warn('attachScrollListener: Table container not found');
    }
  }

  ngOnDestroy() {
    // Remove scroll event listener when component is destroyed
    if (this.scrollHandler && this.tableContainer && this.tableContainer.nativeElement) {
      this.tableContainer.nativeElement.removeEventListener('scroll', this.scrollHandler);
    }
    // Remove document click listener
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler, true);
    }
  }

  handleDocumentClick(event: Event) {
    // Close dropdown if clicking outside the dropdown container
    if (this.showSupplierDropdown) {
      const target = event.target as HTMLElement;
      const dropdownContainer = target.closest('.custom-dropdown-container');
      
      // Close if clicking outside the dropdown container
      if (!dropdownContainer) {
        this.closeSupplierDropdown();
      }
    }
  }

  // Throttle function to limit scroll event calls
  private throttle(func: Function, limit: number) {
    let inThrottle: boolean;
    return function(this: any, ...args: any[]) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  onScroll() {
    if (!this.tableContainer || !this.tableContainer.nativeElement) {
      return;
    }

    const container = this.tableContainer.nativeElement;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Load more when scrolled within 100px of bottom
    const threshold = 100;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    const isNearBottom = distanceFromBottom <= threshold;
    
    console.log('onScroll:', {
      scrollTop,
      scrollHeight,
      clientHeight,
      distanceFromBottom,
      isNearBottom,
      hasMoreData: this.hasMoreData,
      isLoadingMore: this.isLoadingMore,
      loading: this.loading,
      currentPage: this.currentPage,
      totalPages: this.totalPages
    });
    
    if (isNearBottom && this.hasMoreData && !this.isLoadingMore && !this.loading) {
      console.log('onScroll: Triggering loadMoreCategories');
      this.loadMoreCategories();
    }
  }

  loadMoreCategories() {
    // Don't load if already loading or no more data
    if (this.isLoadingMore || !this.hasMoreData || this.loading) {
      console.log('loadMoreCategories: Skipping - isLoadingMore:', this.isLoadingMore, 'hasMoreData:', this.hasMoreData, 'loading:', this.loading);
      return;
    }

    // Check if there are more pages
    if (this.currentPage < this.totalPages) {
      this.isLoadingMore = true;
      this.ngxLoader.start(); // Start loader for infinite scroll
      const nextPage = this.currentPage + 1;
      
      console.log('loadMoreCategories: Loading page', nextPage, 'of', this.totalPages);
      
      const status = this.selectedStatus || undefined;
      const search = this.searchTerm || undefined;
      
      this.apiService.getCategories(nextPage, this.itemsPerPage, search, status).subscribe({
        next: (response: any) => {
          console.log('loadMoreCategories: Raw response:', response);
          
          // Handle interceptor-wrapped response
          // Interceptor wraps: { success: true, statusCode: 200, message: 'Success', data: { categories: [...], pagination: {...} } }
          let responseData = response;
          
          // Check if response is wrapped by interceptor (has 'data' property and 'success' property)
          if (response && response.success !== undefined && response.data) {
            responseData = response.data;
          } else if (response && response.data && typeof response.data === 'object' && !response.success) {
            // Direct data property without success flag
            responseData = response.data;
          }
          
          console.log('loadMoreCategories: Processed responseData:', responseData);
          
          if (responseData && responseData.categories && Array.isArray(responseData.categories) && responseData.pagination) {
            // Append new categories to existing ones
            const newCategories = responseData.categories.map((cat: any) => {
              if (cat.image && !cat.imageUrl) {
                cat.imageUrl = this.getImageUrl(cat.image);
              }
              return cat;
            });
            
            console.log('loadMoreCategories: Adding', newCategories.length, 'new categories. Total before:', this.categories.length);
            console.log('loadMoreCategories: New categories:', newCategories.map((c: any) => c.name || c._id));
            
            this.categories = [...this.categories, ...newCategories];
            this.filteredCategories = [...this.categories];
            
            this.currentPage = responseData.pagination.currentPage;
            this.totalPages = responseData.pagination.totalPages;
            this.totalItems = responseData.pagination.totalItems;
            
            // Check if there's more data
            this.hasMoreData = this.currentPage < this.totalPages;
            
            console.log('loadMoreCategories: Total after:', this.categories.length, 'Current page:', this.currentPage, 'Total pages:', this.totalPages, 'Has more:', this.hasMoreData);
            
            // Re-attach scroll listener after new data loads
            setTimeout(() => {
              this.attachScrollListener();
            }, 50);
          } else if (Array.isArray(responseData)) {
            // Fallback: handle array response
            const newCategories = responseData.map((cat: any) => {
              if (cat.image && !cat.imageUrl) {
                cat.imageUrl = this.getImageUrl(cat.image);
              }
              return cat;
            });
            this.categories = [...this.categories, ...newCategories];
            this.filteredCategories = [...this.categories];
            this.hasMoreData = false;
            console.log('loadMoreCategories: Array response format, added', newCategories.length, 'categories');
          } else {
            console.warn('loadMoreCategories: Unexpected response format', responseData);
          }
          
          this.isLoadingMore = false;
          this.ngxLoader.stop(); // Stop loader
        },
        error: (err) => {
          console.error('Error loading more categories:', err);
          this.isLoadingMore = false;
          this.ngxLoader.stop(); // Stop loader on error
          this.showErrorNotification(err.error?.message || 'Error loading more categories');
        }
      });
    } else {
      console.log('loadMoreCategories: No more pages. currentPage:', this.currentPage, 'totalPages:', this.totalPages);
      this.hasMoreData = false;
    }
  }

  openAddForm() {
    this.showAddForm = true;
    this.editingCategory = null;
    this.formSubmitted = false; // Reset form submitted flag
    this.categoryForm = {
      name: '',
      description: '',
      suppliers: []
    };
    this.formErrors = {
      name: '',
      description: '',
      image: ''
    };
    this.resetFileInput();
    this.imagePreview = '';
    this.existingImageUrl = '';
    this.uploadedImageKey = '';
    this.uploadedImageUrl = '';
    this.imageUploading = false;
    this.imageUploadError = '';
    // Reset search field and close dropdown when opening add form
    this.supplierSearchTerm = '';
    this.showSupplierDropdown = false;
    // Ensure filteredSuppliers shows all suppliers when dropdown opens
    if (this.suppliers && this.suppliers.length > 0) {
      this.filteredSuppliers = [...this.suppliers];
    }
  }

  loadCategories() {
    this.loading = true;
    this.ngxLoader.start(); // Start loader
    this.currentPage = 1; // Reset to first page
    this.hasMoreData = true; // Reset has more data flag
    const status = this.selectedStatus || undefined;
    const search = this.searchTerm || undefined;
    
    console.log('loadCategories: Loading page', this.currentPage, 'with limit', this.itemsPerPage);
    console.log('loadCategories: Filters - status:', status, 'search:', search);
    
    this.apiService.getCategories(this.currentPage, this.itemsPerPage, search, status).subscribe({
      next: (response: any) => {
        console.log('loadCategories: Raw response:', response);
        
        // Handle interceptor-wrapped response
        // Interceptor wraps: { success: true, statusCode: 200, message: 'Success', data: { categories: [...], pagination: {...} } }
        let responseData = response;
        
        // Check if response is wrapped by interceptor (has 'data' property and 'success' property)
        if (response && response.success !== undefined && response.data) {
          responseData = response.data;
        } else if (response && response.data && typeof response.data === 'object' && !response.success) {
          // Direct data property without success flag
          responseData = response.data;
        }
        
        console.log('loadCategories: Processed responseData:', responseData);
        
        if (responseData && responseData.categories && Array.isArray(responseData.categories) && responseData.pagination) {
          console.log('loadCategories: Received', responseData.categories.length, 'categories from API');
          console.log('loadCategories: Pagination info:', responseData.pagination);
          
          // Process categories to ensure imageUrl is set
          this.categories = responseData.categories.map((cat: any) => {
            if (cat.image && !cat.imageUrl) {
              // Construct image URL from S3 key if imageUrl is missing
              cat.imageUrl = this.getImageUrl(cat.image);
            }
            return cat;
          });
          this.filteredCategories = [...this.categories];
          this.currentPage = responseData.pagination.currentPage;
          this.totalPages = responseData.pagination.totalPages;
          this.totalItems = responseData.pagination.totalItems;
          
          // Check if there's more data
          this.hasMoreData = this.currentPage < this.totalPages;
          
          console.log('loadCategories: Loaded', this.categories.length, 'categories. Current page:', this.currentPage, 'Total pages:', this.totalPages, 'Has more:', this.hasMoreData);
          console.log('loadCategories: Total items from API:', this.totalItems);
          console.log('loadCategories: Categories array:', this.categories.map((c: any) => ({ name: c.name, id: c._id, isActive: c.isActive })));
          console.log('loadCategories: filteredCategories length:', this.filteredCategories.length);
          
          // Re-attach scroll listener after data loads and view updates
          setTimeout(() => {
            this.attachScrollListener();
            console.log('loadCategories: Scroll listener attached after data load');
          }, 200);
        } else if (Array.isArray(responseData)) {
          // Fallback for old API response format (direct array)
          this.categories = responseData.map((cat: any) => {
            if (cat.image && !cat.imageUrl) {
              cat.imageUrl = this.getImageUrl(cat.image);
            }
            return cat;
          });
          this.filteredCategories = [...this.categories];
          this.hasMoreData = false;
          console.log('loadCategories: Array response format, loaded', this.categories.length, 'categories');
        } else {
          // No data or unexpected format
          this.categories = [];
          this.filteredCategories = [];
          this.hasMoreData = false;
          console.log('loadCategories: No data received or unexpected format. responseData:', responseData);
        }
        this.loading = false;
        this.ngxLoader.stop(); // Stop loader
      },
      error: (err) => {
        console.error('Error loading categories:', err);
        this.loading = false;
        this.hasMoreData = false;
        this.ngxLoader.stop(); // Stop loader on error
      }
    });
  }

  onSearch() {
    this.currentPage = 1;
    this.categories = []; // Clear existing categories
    this.filteredCategories = [];
    this.loadCategories();
  }

  applyFilters() {
    this.currentPage = 1;
    this.categories = []; // Clear existing categories
    this.filteredCategories = [];
    this.loadCategories();
  }

  resetFilters() {
    this.searchTerm = '';
    this.selectedStatus = '';
    this.currentPage = 1;
    this.categories = []; // Clear existing categories
    this.filteredCategories = [];
    this.loadCategories();
  }

  /** Returns a string key for the category (handles _id as object or string) so action menu state is correct per row. */
  getCategoryActionKey(category: any): string {
    if (!category) return '';
    const id = category._id;
    return id != null ? String(id) : '';
  }

  toggleActionMenu(categoryIdOrCategory: string | any, event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const key = typeof categoryIdOrCategory === 'string' ? categoryIdOrCategory : this.getCategoryActionKey(categoryIdOrCategory);
    Object.keys(this.showActionMenu).forEach(k => {
      if (k !== key) {
        this.showActionMenu[k] = false;
      }
    });
    this.showActionMenu[key] = !this.showActionMenu[key];
  }

  getCategoryId(category: any): string {
    // Use categoryId from database if available, otherwise fallback to generated ID
    if (category.categoryId) {
      return category.categoryId;
    }
    // Fallback: generate from _id if categoryId doesn't exist
    if (category._id) {
      return `C${category._id.toString().slice(-3).padStart(3, '0')}`;
    }
    return 'C000';
  }

  getImageUrl(imageKey: string): string {
    if (!imageKey) return '';
    
    // If already a full URL, return as is
    if (imageKey.startsWith('http://') || imageKey.startsWith('https://')) {
      return imageKey;
    }
    
    // Construct S3 URL from key
    // The backend uses: https://${BUCKET_NAME}.s3.${AWS_REGION}.amazonaws.com/${fileKey}
    // Default values (should match your .env file)
    const bucketName = 's3bucket-development'; // Should match AWS_S3_BUCKET_NAME
    const region = 'ap-south-1'; // Should match AWS_REGION
    
    // Construct the S3 public URL
    return `https://${bucketName}.s3.${region}.amazonaws.com/${imageKey}`;
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.style.display = 'none';
      const placeholder = img.nextElementSibling as HTMLElement;
      if (placeholder) {
        placeholder.style.display = 'flex';
      }
    }
  }

  onImageSelected(event: any) {
    const file = event.target?.files?.[0];
    if (!file) {
      return;
    }

    // Clear image error when user selects a file
    if (this.formSubmitted) {
      this.formErrors.image = '';
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      const errorMsg = 'Please select a valid image file (jpg, jpeg, or png)';
      this.imageUploadError = errorMsg;
      this.showErrorNotification(errorMsg);
      this.resetFileInput();
      return;
    }

    // Validate file size (10 MB = 10 * 1024 * 1024 bytes)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      const errorMsg = 'File size must be less than 10 MB';
      this.imageUploadError = errorMsg;
      this.showErrorNotification(errorMsg);
      this.resetFileInput();
      return;
    }

    // Clear previous errors
    this.imageUploadError = '';
    this.selectedImageFile = file;
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.imagePreview = e.target.result;
    };
    reader.onerror = () => {
      const errorMsg = 'Error reading file. Please try again.';
      this.imageUploadError = errorMsg;
      this.showErrorNotification(errorMsg);
      this.resetFileInput();
    };
    reader.readAsDataURL(file);

    // Upload image immediately to S3
    this.uploadImageToS3(file);
  }

  uploadImageToS3(file: File) {
    this.imageUploading = true;
    this.imageUploadError = '';

    // Don't delete old uploaded image here - it will be deleted only after successful form submission
    // or when user explicitly removes it via the remove button

    // Upload new image to S3 (without folder name)
    this.apiService.uploadImage(file).subscribe({
      next: (response: any) => {
        console.log('Upload response received:', response);
        
        // Handle response format: { meta: { status, msg }, data: { location } }
        if (response && response.meta && response.meta.status === true && response.data && response.data.location) {
          const locationUrl = response.data.location;
          console.log('Location URL:', locationUrl);
          
          // Extract key from location URL
          // URL format: https://bucket.s3.region.amazonaws.com/folder/filename.ext
          try {
            const urlObj = new URL(locationUrl);
            // Remove leading slash from pathname
            this.uploadedImageKey = urlObj.pathname.substring(1);
          this.uploadedImageUrl = locationUrl;
          this.imageUploading = false;
          this.imageUploadError = ''; // Clear any previous errors
          this.formErrors.image = ''; // Clear image validation error
          console.log('Image uploaded successfully:', {
            key: this.uploadedImageKey,
            url: this.uploadedImageUrl
          });
          } catch (urlError) {
            console.error('Error parsing URL:', urlError);
            // Fallback: try to extract manually
            const urlParts = locationUrl.split('/');
            const bucketIndex = urlParts.findIndex((part: string) => part.includes('.s3.'));
            if (bucketIndex >= 0 && bucketIndex < urlParts.length - 1) {
              this.uploadedImageKey = urlParts.slice(bucketIndex + 1).join('/');
            } else {
              // Last resort: use everything after .com/
              const comIndex = locationUrl.indexOf('.com/');
              if (comIndex >= 0) {
                this.uploadedImageKey = locationUrl.substring(comIndex + 5);
              } else {
                this.uploadedImageKey = '';
              }
            }
            this.uploadedImageUrl = locationUrl;
            this.imageUploading = false;
            this.imageUploadError = '';
            this.formErrors.image = ''; // Clear image validation error
          }
        } else {
          // Handle error response or unexpected format
          const errorMsg = response?.meta?.msg || response?.message || 'Failed to upload image';
          console.error('Upload failed:', errorMsg, response);
          this.imageUploadError = errorMsg;
          this.showErrorNotification(errorMsg);
          this.imageUploading = false;
          this.resetFileInput();
        }
      },
      error: (err: any) => {
        console.error('Error uploading image:', err);
        console.error('Error details:', {
          status: err.status,
          statusText: err.statusText,
          error: err.error,
          message: err.message
        });
        
        // Handle error format: { meta: { status, msg }, data: null }
        let errorMessage = 'Error uploading image. Please try again.';
        
        if (err.error) {
          if (err.error.meta && err.error.meta.msg) {
            errorMessage = err.error.meta.msg;
          } else if (err.error.message) {
            errorMessage = err.error.message;
          } else if (typeof err.error === 'string') {
            errorMessage = err.error;
          }
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        this.imageUploadError = errorMessage;
        this.imageUploading = false;
        // Show error notification for image upload errors
        this.showErrorNotification(errorMessage);
        this.resetFileInput();
      }
    });
  }

  deleteOldImage() {
    if (!this.uploadedImageUrl) return;

    // Extract key from URL for deletion
    const imageKey = this.extractKeyFromUrl(this.uploadedImageUrl);
    if (imageKey) {
      this.apiService.deleteImage(imageKey).subscribe({
        next: (response) => {
          console.log('Old image deleted successfully:', imageKey);
        },
        error: (err) => {
          console.error('Error deleting old image:', err);
          // Don't show error to user, just log it
        }
      });
    }

    // Reset uploaded image data
    this.uploadedImageKey = '';
    this.uploadedImageUrl = '';
  }

  resetFileInput() {
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
    this.selectedImageFile = null;
    this.imagePreview = '';
    this.uploadedImageKey = '';
    this.uploadedImageUrl = '';
    this.imageUploading = false;
    this.imageUploadError = '';
  }

  extractKeyFromUrl(url: string): string {
    if (!url) return '';
    
    // If it's already a key (no http/https), return as is
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return url;
    }
    
    // Extract key from S3 URL
    // Format: https://bucket.s3.region.amazonaws.com/key
    try {
      const urlObj = new URL(url);
      // Remove leading slash from pathname
      return urlObj.pathname.substring(1);
    } catch (error) {
      // Fallback: try to extract manually
      const urlParts = url.split('/');
      const bucketIndex = urlParts.findIndex((part: string) => part.includes('.s3.'));
      if (bucketIndex >= 0 && bucketIndex < urlParts.length - 1) {
        return urlParts.slice(bucketIndex + 1).join('/');
      }
      // Last resort: use everything after .com/
      const comIndex = url.indexOf('.com/');
      if (comIndex >= 0) {
        return url.substring(comIndex + 5);
      }
      return '';
    }
  }

  removeImage() {
    // Delete uploaded image from S3 if exists
    console.log('Removing image:', this.uploadedImageUrl);
    if (this.uploadedImageUrl) {
      const imageKey = this.extractKeyFromUrl(this.uploadedImageUrl);
      if (imageKey) {
        this.apiService.deleteImage(imageKey).subscribe({
          next: () => {
            console.log('Image deleted from S3:', imageKey);
          },
          error: (err) => {
            console.error('Error deleting image from S3:', err);
          }
        });
      }
    }

    this.selectedImageFile = null;
    this.imagePreview = '';
    this.existingImageUrl = '';
    this.uploadedImageKey = '';
    this.uploadedImageUrl = '';
    this.imageUploading = false;
    this.imageUploadError = '';
    this.formErrors.image = ''; // Clear image validation error
    this.resetFileInput();
  }

  validateForm(): boolean {
    let isValid = true;
    this.formErrors = {
      name: '',
      description: '',
      image: ''
    };

    // Validate category name
    if (!this.categoryForm.name || !this.categoryForm.name.trim()) {
      this.formErrors.name = 'Category name is required';
      isValid = false;
    }

    // Validate description
    if (!this.categoryForm.description || !this.categoryForm.description.trim()) {
      this.formErrors.description = 'Description is required';
      isValid = false;
    }

    // Validate image - must have either uploaded image (for new category) or existing image (for edit)
    // For new category, uploadedImageUrl is required
    // For edit, either uploadedImageUrl (new upload) or existingImageUrl (keep existing) is acceptable
    if (!this.editingCategory) {
      // New category - must have uploaded image
      if (!this.uploadedImageUrl) {
        this.formErrors.image = 'Image is required';
        isValid = false;
      }
    } else {
      // Editing category - must have either new uploaded image or existing image
      if (!this.uploadedImageUrl && !this.existingImageUrl) {
        this.formErrors.image = 'Image is required';
        isValid = false;
      }
    }

    // Check if image is still uploading
    if (this.imageUploading) {
      this.formErrors.image = 'Please wait for image upload to complete';
      isValid = false;
    }

    // Check if there was an upload error
    if (this.imageUploadError && this.selectedImageFile) {
      this.formErrors.image = 'Please fix image upload error before submitting';
      isValid = false;
      // Show error notification if image upload failed
      this.showErrorNotification(this.imageUploadError);
    }

    return isValid;
  }

  // Clear error for specific field when user starts typing
  clearFieldError(fieldName: 'name' | 'description' | 'image') {
    if (this.formSubmitted) {
      // Only clear if form has been submitted (errors are showing)
      if (fieldName === 'name') {
        if (this.categoryForm.name && this.categoryForm.name.trim()) {
          this.formErrors.name = '';
        }
      } else if (fieldName === 'description') {
        if (this.categoryForm.description && this.categoryForm.description.trim()) {
          this.formErrors.description = '';
        }
      } else if (fieldName === 'image') {
        if (this.uploadedImageUrl || this.existingImageUrl) {
          this.formErrors.image = '';
        }
      }
    }
  }

  saveCategory() {
    // Mark form as submitted to show validation errors
    this.formSubmitted = true;
    
    // Validate form
    if (!this.validateForm()) {
      return;
    }

    this.formLoading = true;
    
    // Send category data with S3 image URL (not the key)
    // Use uploadedImageUrl if new image was uploaded, otherwise use existing image
    const imageUrl = this.uploadedImageUrl || this.existingImageUrl || '';
    const categoryData = {
      name: this.categoryForm.name.trim(),
      description: this.categoryForm.description.trim(),
      image: imageUrl, // Send the S3 URL
      suppliers: this.categoryForm.suppliers || [] // Include selected suppliers
    };

    if (this.editingCategory) {
      // Update category - don't delete old image automatically
      // Old images will be cleaned up manually or by backend if needed
      this.apiService.updateCategory(this.editingCategory._id, categoryData).subscribe({
        next: (response) => {
          console.log('Category updated successfully:', response);
          this.showSuccessNotification('Category updated successfully');
          this.loadCategories();
          this.cancelEdit();
          this.formLoading = false;
        },
        error: (err) => {
          console.error('Error updating category:', err);
          const errorMessage = err.error?.message || err.message || 'Error updating category';
          this.showErrorNotification(errorMessage);
          this.formLoading = false;
        }
      });
    } else {
      this.apiService.createCategory(categoryData).subscribe({
        next: (response) => {
          console.log('Category created successfully:', response);
          this.showSuccessNotification('Category saved successfully');
          this.loadCategories();
          this.cancelEdit();
          this.formLoading = false;
        },
        error: (err) => {
          console.error('Error creating category:', err);
          console.error('Error details:', {
            status: err.status,
            statusText: err.statusText,
            error: err.error,
            message: err.message
          });
          
          // If category creation failed, delete the uploaded image from S3
          // if (this.uploadedImageUrl) {
          //   const imageKey = this.extractKeyFromUrl(this.uploadedImageUrl);
          //   if (imageKey) {
          //     this.apiService.deleteImage(imageKey).subscribe({
          //       next: () => console.log('Uploaded image deleted due to category creation failure'),
          //       error: (deleteErr) => console.error('Error deleting uploaded image:', deleteErr)
          //     });
          //   }
          // }
          
          const errorMessage = err.error?.message || err.message || 'Error creating category';
          this.showErrorNotification(errorMessage);
          this.formLoading = false;
        }
      });
    }
  }

  viewCategory(category: any) {
    this.viewingCategory = {
      ...category,
      imageUrl: category.imageUrl || this.getImageUrl(category.image)
    };
    this.showViewModal = true;
    this.closeActionMenu();
  }

  closeViewModal() {
    const shouldRedirectToProductList = this.openedViewFromProductList;
    this.showViewModal = false;
    this.viewingCategory = null;
    this.openedViewFromProductList = false;
    if (shouldRedirectToProductList) {
      this.router.navigate(['/product']);
    }
  }

  /** Get supplier list for view modal (supplierRefId may be ids or populated objects) */
  getViewSupplierList(): { companyName: string; supplierId: string }[] {
    if (!this.viewingCategory || !this.viewingCategory.supplierRefId?.length) {
      return [];
    }
    return this.viewingCategory.supplierRefId.map((s: any) => {
      const id = s._id || s;
      if (s && typeof s === 'object' && (s.companyName !== undefined || s.supplierId !== undefined)) {
        return {
          companyName: s.companyName || s.name || '—',
          supplierId: s.supplierId || '—'
        };
      }
      return {
        companyName: this.getSupplierName(id),
        supplierId: this.getSupplierId(id)
      };
    });
  }

  /** True if current user can see the View option (Admin or Procurement Manager) */
  canViewCategory(): boolean {
    return this.authService.isAdmin() || this.authService.isManager();
  }

  editCategory(category: any) {
    this.editingCategory = category;
    this.formSubmitted = false; // Reset form submitted flag
    this.showAddForm = true; // Show the form
    this.categoryForm = {
      name: category.name,
      description: category.description || '',
      suppliers: category.supplierRefId ? category.supplierRefId.map((s: any) => s._id || s) : (category.suppliers ? category.suppliers.map((s: any) => s._id || s) : [])
    };
    this.formErrors = {
      name: '',
      description: '',
      image: ''
    };
    // Use imageUrl if available, image is already a full URL
    this.existingImageUrl = category.imageUrl || category.image || '';
    this.imagePreview = this.existingImageUrl;
    this.selectedImageFile = null;
    // Store the existing image URL (image is now stored as URL, not key)
    this.uploadedImageKey = ''; // No longer needed, but keep for compatibility
    this.uploadedImageUrl = category.imageUrl || category.image || '';
    this.imageUploading = false;
    this.imageUploadError = '';
    // Reset search field and close dropdown when editing
    this.supplierSearchTerm = '';
    this.showSupplierDropdown = false;
    // Ensure filteredSuppliers shows all suppliers when dropdown opens
    if (this.suppliers && this.suppliers.length > 0) {
      this.filteredSuppliers = [...this.suppliers];
    }
    this.showAddForm = true;
    this.closeActionMenu();
  }

  toggleCategoryStatus(category: any, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const newStatus = !category.isActive;
    this.apiService.updateCategoryStatus(category._id, newStatus).subscribe({
      next: () => {
        this.loadCategories();
        this.closeActionMenu();
        // Show success modal with category name
        const action = newStatus ? 'activated' : 'deactivated';
        this.statusMessage = `${category.name} has been ${action}`;
        this.showStatusModal = true;
        // Auto-close modal after 3 seconds
        setTimeout(() => {
          this.closeStatusModal();
        }, 3000);
      },
      error: (err) => {
        this.showErrorNotification(err.error?.message || 'Error updating category status');
      }
    });
  }

  closeStatusModal() {
    this.showStatusModal = false;
    this.statusMessage = '';
  }

  deleteCategory(id: string) {
    this.categoryToDelete = id;
    this.showDeleteModal = true;
    this.closeActionMenu();
  }

  confirmDelete() {
    if (this.categoryToDelete) {
      this.apiService.deleteCategory(this.categoryToDelete).subscribe({
        next: () => {
          this.loadCategories();
          this.closeDeleteModal();
          this.showSuccessNotification('Category deleted successfully');
        },
        error: (err) => {
          this.showErrorNotification(err.error?.message || 'Error deleting category');
          this.closeDeleteModal();
        }
      });
    }
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.categoryToDelete = null;
  }

  closeActionMenu() {
    Object.keys(this.showActionMenu).forEach(key => {
      this.showActionMenu[key] = false;
    });
  }

  cancelEdit() {
    this.closeSupplierDropdown();
    // If there's an uploaded image but form is cancelled, delete it from S3
    // if (this.uploadedImageUrl && !this.editingCategory) {
    //   // Only delete if it's a new upload (not editing existing category)
    //   const imageKey = this.extractKeyFromUrl(this.uploadedImageUrl);
    //   if (imageKey) {
    //     this.apiService.deleteImage(imageKey).subscribe({
    //       next: () => console.log('Uploaded image deleted due to form cancellation'),
    //       error: (err) => console.error('Error deleting uploaded image:', err)
    //     });
    //   }
    // }

    this.closeSupplierDropdown();
    this.showAddForm = false;
    this.editingCategory = null;
    this.formSubmitted = false; // Reset form submitted flag
    this.categoryForm = {
      name: '',
      description: '',
      suppliers: []
    };
    this.formErrors = {
      name: '',
      description: '',
      image: ''
    };
    this.resetFileInput();
    this.imagePreview = '';
    this.existingImageUrl = '';
    this.uploadedImageKey = '';
    this.uploadedImageUrl = '';
    this.imageUploading = false;
    this.imageUploadError = '';
    // Reset search field when canceling
    this.supplierSearchTerm = '';
    this.showSupplierDropdown = false;
    // Reset filtered suppliers
    if (this.suppliers && this.suppliers.length > 0) {
      this.filteredSuppliers = [...this.suppliers];
    }
  }


  showSuccessNotification(message: string) {
    this.successMessage = message;
    this.errorMessage = ''; // Clear any error message
    // Auto-hide notification after 3 seconds
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  showErrorNotification(message: string) {
    this.errorMessage = message;
    this.successMessage = ''; // Clear any success message
    // Auto-hide notification after 5 seconds (longer for errors)
    setTimeout(() => {
      this.errorMessage = '';
    }, 5000);
  }
}
